import React from 'react';
import { Star, Sparkles, TrendingUp } from 'lucide-react';

// Points display badge for headers
export const PointsBadge = ({ points, size = 'default' }) => {
  const sizeClasses = {
    small: 'px-2 py-0.5 text-xs gap-1',
    default: 'px-3 py-1 text-sm gap-1.5',
    large: 'px-4 py-1.5 text-base gap-2'
  };
  
  const iconSizes = {
    small: 'w-3 h-3',
    default: 'w-4 h-4',
    large: 'w-5 h-5'
  };
  
  return (
    <div className={`flex items-center bg-yellow-100 rounded-full ${sizeClasses[size]}`}>
      <Star className={`${iconSizes[size]} text-yellow-600`} fill="currentColor" />
      <span className="font-bold text-yellow-700">{points ?? 0}</span>
    </div>
  );
};

// Floating stars animation component
const FloatingStars = () => (
  <div className="absolute inset-0 pointer-events-none overflow-hidden">
    {[...Array(12)].map((_, i) => (
      <div
        key={i}
        className="absolute animate-float-up"
        style={{
          left: `${10 + (i * 7) % 80}%`,
          animationDelay: `${i * 0.15}s`,
          animationDuration: `${1.5 + (i % 3) * 0.3}s`,
        }}
      >
        <Star 
          className="text-yellow-400" 
          fill="currentColor"
          style={{ 
            width: `${14 + (i % 4) * 4}px`, 
            height: `${14 + (i % 4) * 4}px`,
            opacity: 0.8 - (i % 3) * 0.2
          }} 
        />
      </div>
    ))}
  </div>
);

// Celebration popup when points are earned - with cool animation
export const PointsCelebration = ({ show, points, message, onClose, currentTotal }) => {
  const [animating, setAnimating] = React.useState(false);
  const [showCounter, setShowCounter] = React.useState(false);
  const [displayPoints, setDisplayPoints] = React.useState(currentTotal - points);
  
  React.useEffect(() => {
    if (show && points) {
      setAnimating(false);
      setShowCounter(false);
      setDisplayPoints(currentTotal - points);
    }
  }, [show, points, currentTotal]);
  
  const handleAwesome = () => {
    setAnimating(true);
    setShowCounter(true);
    
    // Animate the counter
    const startValue = currentTotal - points;
    const endValue = currentTotal;
    const duration = 1200;
    const steps = 30;
    const increment = (endValue - startValue) / steps;
    let current = startValue;
    let step = 0;
    
    const timer = setInterval(() => {
      step++;
      current = Math.min(startValue + (increment * step), endValue);
      setDisplayPoints(Math.round(current));
      
      if (step >= steps) {
        clearInterval(timer);
        setTimeout(() => {
          onClose();
        }, 600);
      }
    }, duration / steps);
  };
  
  if (!show || !points) return null;
  
  return (
    <div 
      className="fixed inset-0 bg-black/50 z-[100] flex items-center justify-center animate-in fade-in duration-200"
      onClick={onClose}
    >
      <div 
        className={`bg-white rounded-3xl p-6 mx-6 text-center max-w-xs relative overflow-hidden ${
          animating ? 'animate-pulse-once' : 'animate-in zoom-in duration-300'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Floating stars animation when animating */}
        {animating && <FloatingStars />}
        
        {/* Background glow */}
        <div className={`absolute -top-20 -left-20 w-40 h-40 bg-yellow-200/50 rounded-full blur-3xl transition-all duration-500 ${animating ? 'scale-150 opacity-100' : 'scale-100 opacity-50'}`} />
        <div className={`absolute -bottom-20 -right-20 w-40 h-40 bg-primary/20 rounded-full blur-3xl transition-all duration-500 ${animating ? 'scale-150 opacity-100' : 'scale-100 opacity-50'}`} />
        
        <div className="relative">
          <div className="relative mb-2">
            <Sparkles className={`w-12 h-12 text-yellow-400 mx-auto ${animating ? 'animate-spin-slow' : 'animate-pulse'}`} />
          </div>
          
          {/* Points earned display */}
          <div className={`flex items-center justify-center gap-2 my-3 transition-all duration-300 ${animating ? 'scale-110' : ''}`}>
            <Star className="w-6 h-6 text-yellow-500" fill="currentColor" />
            <span className={`text-4xl font-bold text-gradient ${animating ? 'animate-bounce' : ''}`}>+{points}</span>
          </div>
          
          <p className="text-gray-600 font-medium">{message || 'Points earned!'}</p>
          
          {/* Counter animation - shows when clicking Awesome */}
          {showCounter && (
            <div className="mt-4 p-3 bg-gradient-to-r from-yellow-50 to-orange-50 rounded-xl animate-in slide-in-from-bottom duration-300">
              <div className="flex items-center justify-center gap-2">
                <TrendingUp className="w-5 h-5 text-green-500 animate-bounce" />
                <span className="text-sm text-gray-500">New Balance:</span>
              </div>
              <div className="flex items-center justify-center gap-2 mt-1">
                <Star className="w-5 h-5 text-yellow-500" fill="currentColor" />
                <span className="text-2xl font-bold text-yellow-600 tabular-nums">
                  {displayPoints}
                </span>
              </div>
            </div>
          )}
          
          {!animating && (
            <button 
              onClick={handleAwesome}
              className="mt-4 px-8 py-3 gradient-brand text-white rounded-full text-sm font-semibold hover:shadow-lg transition-all duration-300 hover:scale-105"
            >
              Awesome! ✨
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default PointsBadge;
